/**
 * AuditReportDetail — full PDF-style printable audit report.
 *
 * Covers every data point returned by the audit API:
 *   - Score overview (overall + 3 pillars)
 *   - Technical signals
 *   - Schema & structured data
 *   - Content / FAQ analysis
 *   - AI platform verdicts
 *   - All active recommendations
 *
 * Uses Tailwind `print:` variants so the sidebar is hidden and the report
 * fills the full page when printed / saved as PDF.
 */

import { useState, useEffect } from 'react';
import type { AuditResponse, AuditSuggestion, RescoreResponse } from '../types';
import { CitationHistorySection } from './CitationHistorySection';
import { getScoreLabel } from '@fixmyseo/shared/label-bands';
import { api } from '../api';

// ── Helpers ────────────────────────────────────────────────────────────────

const DIFFICULTY_LABELS: Record<AuditSuggestion['difficulty'], string> = {
  easy: 'Quick win',
  medium: 'Some effort',
  hard: 'Longer project',
};

const CATEGORY_LABELS: Record<AuditSuggestion['category'], string> = {
  technical: 'Technical',
  geo: 'Directory / GEO',
  authority: 'Authority',
  seo: 'SEO',
  aeo: 'AEO',
  directory: 'Directory',
};

function scoreColour(score: number): string {
  if (score >= 70) return '#1D9E75';
  if (score >= 40) return '#EF9F27';
  return '#EF4444';
}

function scoreLabel(score: number): string {
  return getScoreLabel(score);
}

function fmt(d: string): string {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ScoreCircle({ score, label, size = 'md', color }: { score: number; label: string; size?: 'sm' | 'md' | 'lg'; color?: string }) {
  const r = size === 'lg' ? 48 : size === 'md' ? 36 : 28;
  const sw = size === 'lg' ? 9 : size === 'md' ? 7 : 5;
  const dim = (r + sw) * 2;
  const cx = dim / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const colour = color ?? scoreColour(score);
  const numSize = size === 'lg' ? '26px' : size === 'md' ? '20px' : '15px';
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative inline-flex items-center justify-center">
        <svg width={dim} height={dim} className="-rotate-90">
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="#E5E0D5" strokeWidth={sw} />
          <circle cx={cx} cy={cx} r={r} fill="none" stroke={colour} strokeWidth={sw}
            strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
        </svg>
        <span className="absolute" style={{ color: colour, fontFamily: 'Lora, Georgia, serif', fontWeight: 500, fontSize: numSize, fontVariantNumeric: 'tabular-nums' }}>{score}</span>
      </div>
      <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7A8783' }}>{label}</span>
    </div>
  );
}

function SectionHeading({ number, title }: { number: string; title: string }) {
  return (
    <div className="flex items-center gap-3 mb-6 pb-3 border-b border-brand-ink/10">
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', background: '#ECFDF5', fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '11px', color: '#059669', flexShrink: 0 }}>{number}</span>
      <h2 style={{ fontFamily: 'Lora, Georgia, serif', fontWeight: 500, fontSize: '17px', color: '#1A2E2A', letterSpacing: '-0.01em', margin: 0 }}>{title}</h2>
    </div>
  );
}

function SignalRow({ label, description, pass }: { label: string; description: string; pass: boolean }) {
  return (
    <div className="flex items-start gap-4 py-3 border-b border-brand-ink/5 last:border-0">
      <span className={`mt-0.5 text-sm font-bold shrink-0 w-5 text-center ${pass ? 'text-brand-green' : 'text-red-500'}`}>
        {pass ? '✓' : '✗'}
      </span>
      <div>
        <p className={`text-sm font-medium ${pass ? 'text-brand-ink' : 'text-red-600'}`}>{label}</p>
        <p className="text-xs text-brand-ink/50 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

function RecommendationRow({ s, rank }: { s: AuditSuggestion; rank: number }) {
  const isQuickWin = s.difficulty === 'easy';
  const pillBg = isQuickWin ? '#ECFDF5' : '#F5F3EC';
  const pillColor = isQuickWin ? '#059669' : '#4A5A56';
  const pillBorder = isQuickWin ? 'none' : '1px solid #E5E0D5';
  return (
    <div className="flex items-start gap-4 py-3 border-b border-brand-ink/8 last:border-0">
      <span style={{ fontFamily: 'Lora, Georgia, serif', fontWeight: 500, fontSize: '14px', color: '#7A8783', width: 24, flexShrink: 0, paddingTop: 2, fontVariantNumeric: 'tabular-nums' }}>
        {String(rank).padStart(2, '0')}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <h4 style={{ fontFamily: 'Lora, Georgia, serif', fontWeight: 500, fontSize: '14px', color: '#1A2E2A', lineHeight: 1.35, margin: 0 }}>{s.title}</h4>
          <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 9px', borderRadius: 100, flexShrink: 0, background: pillBg, color: pillColor, border: pillBorder }}>
            {DIFFICULTY_LABELS[s.difficulty]}
          </span>
        </div>
        <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '11px', color: '#7A8783', marginTop: 4, lineHeight: 1.5 }}>
          {CATEGORY_LABELS[s.category]} · Impact {s.impact}/10
        </p>
        <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#4A5A56', marginTop: 6, lineHeight: 1.6 }}>{s.description}</p>
      </div>
    </div>
  );
}

// ── Sub-score breakdown config (A4 + A7) ───────────────────────────────────

type Velocity = 'fast' | 'slow';

interface SubScoreConfig<K extends string> {
  key: K;
  label: string;
  weight: number;
  velocity: Velocity;
}

const SEO_SUB_SCORES: SubScoreConfig<string>[] = [
  { key: 'contentQuality',       label: 'Content Quality',        weight: 30, velocity: 'fast' },
  { key: 'technicalHealth',      label: 'Technical Health',       weight: 25, velocity: 'fast' },
  { key: 'authoritySignals',     label: 'Authority Signals',      weight: 22, velocity: 'slow' },
  { key: 'contentFreshness',     label: 'Content Freshness',      weight: 10, velocity: 'fast' },
  { key: 'runtimeCompatibility', label: 'Runtime Compatibility',  weight:  8, velocity: 'fast' },
  { key: 'brandWebMentions',     label: 'Brand Mentions',         weight:  3, velocity: 'slow' },
];

const AEO_SUB_SCORES: SubScoreConfig<string>[] = [
  { key: 'aiOverviewPresence',     label: 'AI Overview Presence',   weight: 40, velocity: 'slow' },
  { key: 'featuredSnippetCapture', label: 'Featured Snippets',      weight: 25, velocity: 'slow' },
  { key: 'knowledgePanelSignals',  label: 'Knowledge Panel',        weight: 15, velocity: 'slow' },
  { key: 'paaPresence',            label: 'People Also Ask',        weight: 10, velocity: 'slow' },
  { key: 'runtimeCompatibility',   label: 'Runtime Compatibility',  weight: 10, velocity: 'fast' },
];

const GEO_SUB_SCORES: SubScoreConfig<string>[] = [
  { key: 'citationRate',             label: 'Citation Rate',          weight: 20, velocity: 'slow' },
  { key: 'runtimeCompatibility',     label: 'Runtime Compatibility',  weight: 15, velocity: 'fast' },
  { key: 'brandWebMentions',         label: 'Brand Mentions',         weight: 15, velocity: 'slow' },
  { key: 'mentionShare',             label: 'Mention Share',          weight: 10, velocity: 'slow' },
  { key: 'contentFreshness',         label: 'Content Freshness',      weight: 10, velocity: 'fast' },
  { key: 'platformAffinity',         label: 'Platform Affinity',      weight: 10, velocity: 'slow' },
  { key: 'structuredContentDensity', label: 'Structured Content',     weight: 10, velocity: 'fast' },
  { key: 'aiCrawlerAccessibility',   label: 'AI Crawler Access',      weight: 10, velocity: 'fast' },
];

function SubScoreRow({ label, score, weight, velocity, lowConfidence }: {
  label: string; score: number; weight: number; velocity: Velocity; lowConfidence: boolean;
}) {
  const colour = scoreColour(score);
  return (
    <div className="flex items-center gap-3 py-2 border-b border-brand-ink/5 last:border-0">
      <div style={{ width: 170, flexShrink: 0 }}>
        <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '12px', color: '#1A2E2A' }}>{label}</span>
        {lowConfidence && (
          <span style={{ marginLeft: 6, fontSize: '9px', fontFamily: 'Poppins, sans-serif', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', background: '#FEF3C7', color: '#92400E', padding: '1px 5px', borderRadius: 4 }}>est.</span>
        )}
      </div>
      <span style={{ width: 30, flexShrink: 0, fontFamily: 'Poppins, sans-serif', fontSize: '10px', color: '#7A8783', textAlign: 'right' }}>{weight}%</span>
      <div style={{ flex: 1, height: 5, background: '#E5E0D5', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${score}%`, height: '100%', background: colour, borderRadius: 3 }} />
      </div>
      <span style={{ width: 28, flexShrink: 0, textAlign: 'right', fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '12px', color: colour, fontVariantNumeric: 'tabular-nums' }}>{score}</span>
      <span style={{
        width: 32, flexShrink: 0, textAlign: 'center',
        fontSize: '9px', fontFamily: 'Poppins, sans-serif', fontWeight: 500, letterSpacing: '0.05em',
        textTransform: 'uppercase', borderRadius: 4, padding: '1px 4px',
        background: velocity === 'fast' ? '#ECFDF5' : '#F5F3EC',
        color: velocity === 'fast' ? '#059669' : '#7A6A4A',
      }}>
        {velocity}
      </span>
    </div>
  );
}

function SubScorePanel({
  title, colour, configs, subScores,
}: {
  title: string; colour: string;
  configs: SubScoreConfig<string>[];
  subScores: Record<string, { score: number; lowConfidence: boolean }>;
}) {
  return (
    <div className="rounded-xl border border-brand-ink/8 overflow-hidden mb-4">
      <div style={{ background: `${colour}12`, padding: '8px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: colour }}>{title}</span>
      </div>
      <div style={{ padding: '0 16px' }}>
        {configs.map((c) => {
          const ss = subScores[c.key];
          if (!ss) return null;
          return (
            <SubScoreRow
              key={c.key}
              label={c.label}
              score={ss.score}
              weight={c.weight}
              velocity={c.velocity}
              lowConfidence={ss.lowConfidence}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────

interface Props {
  data: AuditResponse;
  savedAt: string;
  onBack: () => void;
  domainId: string;
}

export function AuditReportDetail({ data, savedAt, onBack, domainId }: Props) {
  const overall = data.scores.overall;
  const colour = scoreColour(overall);

  const [rescore, setRescore] = useState<RescoreResponse | null>(null);

  useEffect(() => {
    const id = data.auditId;
    if (!id) return;
    api.audits.rescoreOnRead(id).then(setRescore).catch(() => { /* non-fatal */ });
  }, [data.auditId]);

  const technicalSignals: Array<{ label: string; description: string; pass: boolean }> = [
    {
      label: 'HTTPS encryption',
      description: data.technical.isHttps
        ? 'Your site is served over HTTPS. All connections are encrypted.'
        : 'Your site is served over plain HTTP. Browsers show a "Not Secure" warning, and AI engines deprioritise HTTP sites.',
      pass: data.technical.isHttps,
    },
    {
      label: 'Mobile viewport meta tag',
      description: data.technical.hasMobileViewport
        ? 'A valid mobile viewport tag is present. Your site renders correctly on all devices.'
        : 'No mobile viewport meta tag detected. The page may render poorly on mobile and score badly in mobile-first indexing.',
      pass: data.technical.hasMobileViewport,
    },
    {
      label: 'AI crawler access (robots.txt)',
      description: data.technical.robotsBlocksAiBots
        ? `Your robots.txt blocks the following AI crawlers: ${data.technical.blockedBots.join(', ')}. These bots cannot index your content for AI training and recommendations.`
        : 'AI crawlers (GPTBot, Google-Extended, PerplexityBot, etc.) can access your site. Your robots.txt allows AI indexing.',
      pass: !data.technical.robotsBlocksAiBots,
    },
    {
      label: 'Page load speed',
      description: (() => {
        const lcpMs = data.pageSpeed?.mobile?.lcpMs ?? null;
        if (lcpMs !== null) {
          return lcpMs <= 2500
            ? `LCP ${lcpMs}ms — within the good threshold (≤2,500ms). Users experience fast page loads on mobile.`
            : lcpMs <= 4000
              ? `LCP ${lcpMs}ms — needs improvement (good: ≤2,500ms). Slow page loads reduce AI engine confidence in site quality.`
              : `LCP ${lcpMs}ms — poor (good: ≤2,500ms). Slow page loads significantly reduce AI recommendation probability.`;
        }
        if (data.technical.pageSpeedMs !== null) {
          return `TTFB ${data.technical.pageSpeedMs}ms. ${data.technical.pageSpeedMs < 3000 ? 'Under the 3-second threshold.' : 'Over the 3-second threshold. Slow load times reduce AI engine confidence in your site quality.'}`;
        }
        return 'Page load speed could not be measured during this audit.';
      })(),
      pass: (() => {
        const lcpMs = data.pageSpeed?.mobile?.lcpMs ?? null;
        if (lcpMs !== null) return lcpMs <= 2500;
        return data.technical.pageSpeedMs === null || data.technical.pageSpeedMs < 3000;
      })(),
    },
  ];

  const schemaSignals: Array<{ label: string; description: string; pass: boolean }> = [
    {
      label: 'Structured data / schema markup',
      description: data.schema.hasRelevantSchema
        ? `Relevant schema markup detected. Types found: ${data.schema.typesFound.length > 0 ? data.schema.typesFound.join(', ') : 'generic web markup'}.`
        : 'No relevant business schema detected. Schema markup (JSON-LD) helps AI engines understand your business, services, and location.',
      pass: data.schema.hasRelevantSchema,
    },
    {
      label: 'Directory / authority links (sameAs)',
      description: data.schema.sameAsCount > 0
        ? `${data.schema.sameAsCount} directory/authority link${data.schema.sameAsCount > 1 ? 's' : ''} found in schema sameAs declarations (e.g. Google Business Profile, Yelp).`
        : 'No directory or authority links found in schema markup. sameAs links build trust signals used by AI recommendation engines.',
      pass: data.schema.sameAsCount >= 2,
    },
  ];

  const contentSignals: Array<{ label: string; description: string; pass: boolean }> = [
    {
      label: `FAQ content (${data.faqs.pairCount} Q&A pairs detected)`,
      description: data.faqs.meetsAeoThreshold
        ? `${data.faqs.pairCount} FAQ pairs detected — meets the AEO threshold of 3+ pairs. AI engines use FAQ content as source material for answers.`
        : `Only ${data.faqs.pairCount} FAQ pair${data.faqs.pairCount === 1 ? '' : 's'} detected. To meet the AEO threshold, your pages should contain at least 3 clear question-and-answer pairs.`,
      pass: data.faqs.meetsAeoThreshold,
    },
    {
      label: 'FAQ schema markup',
      description: data.faqs.hasFaqSchema
        ? 'FAQPage schema markup is present. Google can surface your Q&As directly in search and AI-powered overviews.'
        : 'No FAQPage schema detected. Adding JSON-LD FAQPage schema enables AI search engines to surface your answers directly.',
      pass: data.faqs.hasFaqSchema,
    },
  ];

  const allPassed = [...technicalSignals, ...schemaSignals, ...contentSignals].filter(s => s.pass).length;
  const allTotal  = technicalSignals.length + schemaSignals.length + contentSignals.length;

  return (
    <div className="min-h-screen bg-white print:bg-white">
      {/* ── Toolbar (hidden on print) ── */}
      <div className="print:hidden sticky top-0 z-10 flex items-center justify-between px-6 py-3 bg-brand-paper border-b border-brand-ink/10">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-brand-ink/50 hover:text-brand-ink transition-colors">
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          All reports
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6v-8z" />
          </svg>
          Save as PDF
        </button>
      </div>

      {/* ── Report body ── */}
      <div className="max-w-3xl mx-auto px-8 py-10 print:px-12 print:py-8">

        {/* ── Cover header ── */}
        <div className="mb-10 pb-8 border-b-2 border-brand-ink/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#059669', marginBottom: 8 }}>AI Visibility Audit Report</p>
              <h1 style={{ fontFamily: 'Lora, Georgia, serif', fontWeight: 500, fontSize: '28px', color: '#1A2E2A', letterSpacing: '-0.015em', wordBreak: 'break-all', lineHeight: 1.2, margin: 0 }}>{data.domain}</h1>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400, fontSize: '11px', color: '#7A8783', marginTop: 8 }}>Audited on {fmt(savedAt)} · Report generated by FixMySEO</p>
            </div>
            <div className="shrink-0">
              <ScoreCircle score={overall} label="AI Visibility Score" size="lg" />
            </div>
          </div>
          {/* Verdict callout — only shown for warning / critical scores */}
          {overall < 60 && (
          <div className="mt-6 p-4 rounded-xl border-l-4" style={{ borderColor: colour, backgroundColor: `${colour}10` }}>
            <p style={{ fontFamily: 'Lora, Georgia, serif', fontStyle: 'italic', fontWeight: 500, fontSize: '15px', color: colour }}>
              {scoreLabel(overall)}: {overall}/100
            </p>
            <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: '#4A5A56', marginTop: 6, lineHeight: 1.6 }}>
              {overall >= 40
                ? 'Your site has a reasonable base but meaningful gaps that are limiting AI recommendations. The items below are your highest-leverage improvements.'
                : 'Critical gaps are preventing AI engines from indexing and recommending your business. Prioritise the technical issues below immediately.'}
            </p>
          </div>
          )}
        </div>

        {/* ── Section 1: Score breakdown ── */}
        <section className="mb-10 print:break-inside-avoid">
          <SectionHeading number="01" title="Score Breakdown" />
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="flex flex-col items-center p-4 rounded-xl text-center" style={{ border: '1px solid #E5E0D5' }}>
              <ScoreCircle score={data.scores.seo} label="SEO" size="md" color="#059669" />
              {rescore && (() => { const d = Math.round(rescore.rescored.seo.pillarScore - (rescore.storedScores.seo ?? 0)); return d !== 0 ? <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '10px', fontWeight: 600, color: d > 0 ? '#059669' : '#DC2626', marginTop: 4 }}>{d > 0 ? `+${d}` : d} pts since last audit</span> : null; })()}
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '10px', color: '#7A8783', marginTop: 10, lineHeight: 1.35 }}>Technical foundations — HTTPS, mobile, speed, crawlability</p>
            </div>
            <div className="flex flex-col items-center p-4 rounded-xl text-center" style={{ border: '1px solid #E5E0D5' }}>
              <ScoreCircle score={data.scores.aeo} label="AEO" size="md" color="#D4A84B" />
              {rescore && (() => { const d = Math.round(rescore.rescored.aeo.pillarScore - (rescore.storedScores.aeo ?? 0)); return d !== 0 ? <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '10px', fontWeight: 600, color: d > 0 ? '#059669' : '#DC2626', marginTop: 4 }}>{d > 0 ? `+${d}` : d} pts since last audit</span> : null; })()}
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '10px', color: '#7A8783', marginTop: 10, lineHeight: 1.35 }}>Answer readiness — FAQs, structured data, direct-answer content</p>
            </div>
            <div className="flex flex-col items-center p-4 rounded-xl text-center" style={{ border: '1px solid #E5E0D5' }}>
              <ScoreCircle score={data.scores.geo} label="GEO" size="md" color="#E8825C" />
              {rescore && (() => { const d = Math.round(rescore.rescored.geo.pillarScore - (rescore.storedScores.geo ?? 0)); return d !== 0 ? <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '10px', fontWeight: 600, color: d > 0 ? '#059669' : '#DC2626', marginTop: 4 }}>{d > 0 ? `+${d}` : d} pts since last audit</span> : null; })()}
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '10px', color: '#7A8783', marginTop: 10, lineHeight: 1.35 }}>AI citation presence — citations, authority signals, directory presence</p>
            </div>
          </div>
          <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '10px', color: '#7A8783', textAlign: 'center' }}>
            {allPassed}/{allTotal} signals passed · Each score 0–100
          </p>

          {/* Sub-score breakdown (A4 + A7) */}
          {rescore ? (
            <div className="mt-6">
              <p style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500, fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7A8783', marginBottom: 12 }}>
                Sub-score breakdown · <span style={{ color: '#059669' }}>fast</span> = responds within days · <span style={{ color: '#7A6A4A' }}>slow</span> = builds over weeks
              </p>
              <SubScorePanel
                title="SEO"
                colour="#059669"
                configs={SEO_SUB_SCORES}
                subScores={rescore.rescored.seo.subScores as unknown as Record<string, { score: number; lowConfidence: boolean }>}
              />
              <SubScorePanel
                title="AEO"
                colour="#D4A84B"
                configs={AEO_SUB_SCORES}
                subScores={rescore.rescored.aeo.subScores as unknown as Record<string, { score: number; lowConfidence: boolean }>}
              />
              <SubScorePanel
                title="GEO"
                colour="#E8825C"
                configs={GEO_SUB_SCORES}
                subScores={rescore.rescored.geo.subScores as unknown as Record<string, { score: number; lowConfidence: boolean }>}
              />
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '10px', color: '#7A8783', marginTop: 4 }}>
                est. = estimated with limited data · Deployed fixes update fast signals within 2–7 days; slow signals (authority, citations) build over weeks.
              </p>
              <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '9px', color: '#AAB4B1', marginTop: 6 }}>
                Scored under <a href="https://github.com/Cloud-Wifi/fixmyseo-standards" target="_blank" rel="noopener noreferrer" style={{ color: '#AAB4B1', textDecoration: 'underline' }}>FixMySEO Standards {rescore.rescored.seo.methodologyRef.split('/').at(-1) ?? rescore.rescored.seo.methodologyRef}</a>
              </p>
            </div>
          ) : data.auditId ? (
            <div className="mt-6 text-center py-4">
              <div className="inline-block w-4 h-4 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
            </div>
          ) : null}
        </section>

        {/* ── Section 2: Technical analysis ── */}
        <section className="mb-10 print:break-inside-avoid">
          <SectionHeading number="02" title="Technical Analysis" />
          <div className="bg-brand-paper/50 rounded-xl border border-brand-ink/8 px-5 divide-y divide-brand-ink/5">
            {technicalSignals.map((s) => (
              <SignalRow key={s.label} {...s} />
            ))}
          </div>
        </section>

        {/* ── Section 3: Schema & Structured data ── */}
        <section className="mb-10 print:break-inside-avoid">
          <SectionHeading number="03" title="Schema & Structured Data" />
          <div className="bg-brand-paper/50 rounded-xl border border-brand-ink/8 px-5 divide-y divide-brand-ink/5 mb-4">
            {schemaSignals.map((s) => (
              <SignalRow key={s.label} {...s} />
            ))}
          </div>
          {data.schema.typesFound.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="text-xs text-brand-ink/40 mr-1">Schema types found:</span>
              {data.schema.typesFound.map((t) => (
                <span key={t} className="text-xs font-medium bg-brand-green/8 text-brand-green px-2.5 py-0.5 rounded-full border border-brand-green/20">
                  {t}
                </span>
              ))}
            </div>
          )}
        </section>

        {/* ── Section 4: Content analysis ── */}
        <section className="mb-10 print:break-inside-avoid">
          <SectionHeading number="04" title="Content & AEO Signals" />
          <div className="bg-brand-paper/50 rounded-xl border border-brand-ink/8 px-5 divide-y divide-brand-ink/5">
            {contentSignals.map((s) => (
              <SignalRow key={s.label} {...s} />
            ))}
          </div>
        </section>

        {/* ── Section 5: AI Citation Visibility ── */}
        <section className="mb-10 print:break-inside-avoid">
          <SectionHeading number="05" title="AI Citation Visibility" />
          <CitationHistorySection domainId={domainId} />
        </section>

        {/* ── Section 6: Recommendations ── */}
        {(data.topSuggestions ?? []).length > 0 && (
          <section className="mb-10">
            <SectionHeading number="06" title={`Recommendations (${(data.topSuggestions ?? []).length} active)`} />
            <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: '#7A8783', marginBottom: 20, lineHeight: 1.6 }}>
              Ranked by impact score. Quick wins are listed first within each impact tier.
            </p>
            {(data.topSuggestions ?? []).map((s, i) => (
              <RecommendationRow key={s.key} s={s} rank={i + 1} />
            ))}
            <p style={{ fontFamily: 'Poppins, sans-serif', fontStyle: 'italic', fontSize: '11px', color: '#7A8783', marginTop: 16 }}>
              Quick wins are fast to implement independently. Step-by-step guidance for all items is available on a paid plan.
            </p>
          </section>
        )}

        {/* ── Footer ── */}
        <footer className="pt-6 border-t border-brand-ink/10 text-center">
          <p className="text-xs text-brand-ink/30">
            FixMySEO · AI Visibility Report · {data.domain} · {fmt(savedAt)}
          </p>
          <p className="text-xs text-brand-ink/20 mt-1">
            This report reflects the state of your site at the time of the audit. Scores and signals may change as you make improvements.
          </p>
        </footer>

      </div>
    </div>
  );
}
